import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';
import { toast } from 'react-toastify';
import { SortOrder, sortRows } from '../../../utils/sortUtils';
import ColumnFilterDropdown from './ColumnFilterDropdown';
import TablePaginationFooter from './TablePaginationFooter';
import TableToolbar from './TableToolbar';

interface ProviderDocumentsGridProps {}

// Constantes e helpers para filtro de valores vazios
const EMPTY_FILTER_VALUE = '__EMPTY__';
const EMPTY_FILTER_LABEL = '(Vazio)';
const isEmptyValue = (v: any): boolean => v === null || v === undefined || v === '';

const ProviderDocumentsGrid: React.FC<ProviderDocumentsGridProps> = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.document;
    
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState<any[]>([]);
    const [filteredReports, setFilteredReports] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [filters, setFilters] = useState<Record<string, any[]>>({});
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    // AF-3418: ordenação (as colunas já eram declaradas sortable, mas nada ordenava) e
    // busca livre em todas as colunas
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState<SortOrder>('ASC');
    const [search, setSearch] = useState('');

    const toggleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
        } else {
            setSortBy(field);
            setSortOrder('ASC');
        }
        setCurrentPage(0);
    };

    const setFilterValues = (columnId: string, values: any[]) => {
        setFilters(prev => {
            if (values.length === 0) {
                const next = { ...prev };
                delete next[columnId];
                return next;
            }
            return { ...prev, [columnId]: values };
        });
    };

    useEffect(() => {
        if (batchId && document) {
            loadReports();
        }
    }, [batchId, document]);

    const loadReports = async () => {
        try {
            setLoading(true);
            
            const cachedBatchData = location.state?.batchData;
            let batchData;
            
            if (cachedBatchData && cachedBatchData.reports) {
                batchData = cachedBatchData;
            } else {
                batchData = await recommendationBFFService.analyzeBatch(batchId!);
            }
            
            if (!batchData || !batchData.reports) {
                toast.error('Nenhum dado encontrado para este lote');
                return;
            }

            const documentReports = batchData.reports.filter((r: any) => r.document === document);
            
            if (documentReports.length === 0) {
                toast.warning('Nenhum processo encontrado para este documento');
                return;
            }

            setReports(documentReports);
            setFilteredReports(documentReports);
            generateColumns(documentReports);
            
        } catch (error) {
            console.error('Erro ao carregar reports:', error);
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const generateColumns = (reports: any[]) => {
        if (reports.length === 0) return;
        
        const allKeys = Object.keys(reports[0]);
        
        // Campos a excluir
        const excludeFields = [
            'document', 'nome', 'backend_pld_flag', 'backend_credit_flag',
            'backend_recommendation', 'backend_reasons', 'backend_risk_score', 
            'backend_analysis_id', 'backend_document_type',
            'status', 'statusFormatted', 'createdAt', 'updatedAt',
            'batchReportId', 'attempts', 'maxAttempts', 'events',
            'reportType', 'reportTypeId', 'analysis_status'
        ];
        
        // Coluna fixa: Recomendação
        const recommendationColumn = {
            id: 'recommendation',
            label: 'Recomendação',
            fixed: true,
            sortable: true,
            filterable: true
        };
        
        // Colunas dinâmicas
        const dynamicColumns = allKeys
            .filter(key => !excludeFields.includes(key))
            .map(key => ({
                id: key,
                label: formatColumnLabel(key),
                sortable: true,
                filterable: true
            }));
        
        setColumns([recommendationColumn, ...dynamicColumns]);
    };

    const formatColumnLabel = (key: string): string => {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    const getRecommendationBadge = (report: any) => {
        const creditFlag = report.backend_credit_flag;
        let recommendation = report.backend_recommendation;
        
        // Normalizar PT → EN
        if (recommendation) {
            const normalized = recommendation.toUpperCase();
            if (normalized === 'APROVADO') recommendation = 'APPROVED';
            else if (normalized === 'PENDENTE') recommendation = 'PENDING';
            else if (normalized === 'REPROVADO' || normalized === 'REJEITADO') recommendation = 'REJECTED';
            else if (normalized === 'ERRO') recommendation = 'ERROR';
            else if (normalized === 'SEM_DADOS') recommendation = 'NO_DATA';
        }
        
        // AF-3460: sem dados tem tratamento próprio, em cinza neutro — não é falha nem
        // violação, então não usa o visual de alerta do erro.
        if (recommendation === 'NO_DATA') {
            return (
                <span
                    className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-300 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-500"
                    title="O provedor respondeu, mas não há dados para este documento"
                >
                    Sem dados
                </span>
            );
        }
        
        // AF-3418: erro é avaliado ANTES do credit_flag. Um documento cuja análise falhou não
        // pode ser exibido como sinalização de crédito nem como pendência.
        if (recommendation === 'ERROR' || !recommendation) {
            return (
                <span
                    className="px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-900 border border-orange-400 dark:bg-orange-900 dark:text-orange-100"
                    title={report.backend_error || 'Análise não concluída'}
                >
                    ⚠️ Erro na análise
                </span>
            );
        }
        
        // Prioridade: credit_flag > recommendation
        if (creditFlag) {
            return (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    🎯 Sinalizado Crédito
                </span>
            );
        }
        
        const badges: Record<string, JSX.Element> = {
            'APPROVED': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">✅ Aprovado</span>,
            'PENDING': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">⏳ Pendente</span>,
            'REJECTED': <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">❌ Reprovado</span>
        };
        
        return badges[recommendation] || badges['PENDING'];
    };

    const formatCellValue = (value: any): string => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    const applyFilters = (reports: any[], activeFilters: Record<string, any[]>) => {
        return reports.filter(report => {
            return Object.entries(activeFilters).every(([columnId, values]) => {
                if (values.length === 0) return true;
                
                // Para a coluna de recomendação
                if (columnId === 'recommendation') {
                    const badge = getRecommendationText(report);
                    const match = values.includes(badge);
                    console.log('Filtro recommendation:', { badge, values, match, report: report.document });
                    return match;
                }
                
                // Para outras colunas: suportar filtro de valor vazio
                return values.includes(report[columnId]) || 
                    (values.includes(EMPTY_FILTER_VALUE) && isEmptyValue(report[columnId]));
            });
        });
    };

    const getRecommendationText = (report: any): string => {
        if (report.backend_credit_flag) return 'Sinalizado Crédito';
        if (report.backend_recommendation === 'SINALIZADO_CREDITO') return 'Sinalizado Crédito';
        if (report.backend_recommendation === 'APROVADO') return 'Aprovado';
        if (report.backend_recommendation === 'REPROVADO') return 'Reprovado';
        if (report.backend_recommendation === 'PENDENTE') return 'Pendente';
        return 'Pendente';
    };

    const handleFilterChange = (columnId: string, value: any, checked: boolean) => {
        setFilters(prev => {
            const columnFilters = prev[columnId] || [];
            
            if (checked) {
                return { ...prev, [columnId]: [...columnFilters, value] };
            } else {
                return { ...prev, [columnId]: columnFilters.filter(v => v !== value) };
            }
        });
    };

    const clearFilter = (columnId: string) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[columnId];
            return newFilters;
        });
    };

    // Aplicar filtros de coluna + busca livre + ordenação
    useEffect(() => {
        let result = applyFilters(reports, filters);

        const term = search.trim().toLowerCase();
        if (term) {
            // Busca em todas as colunas visíveis: com a paginação, o Ctrl+F do navegador
            // só encontraria o que está na página atual.
            result = result.filter(report =>
                columns.some(column => {
                    const value =
                        column.id === 'recommendation'
                            ? getRecommendationText(report)
                            : report[column.id];
                    return !isEmptyValue(value) && String(value).toLowerCase().includes(term);
                })
            );
        }

        if (sortBy) {
            result = sortRows(
                result,
                sortBy,
                sortOrder,
                {},
                (report, field) =>
                    field === 'recommendation' ? getRecommendationText(report) : report[field]
            );
        }

        setFilteredReports(result);
        setCurrentPage(0); // Reset para primeira página
        // getRecommendationText/applyFilters são estáveis dentro do componente
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, reports, search, sortBy, sortOrder, columns]);

    /**
     * Contagem de linhas por valor, exibida ao lado de cada opção do filtro.
     * Calculada sobre os reports sem filtro, para o número não mudar conforme se filtra.
     */
    const valueCountsByColumn = React.useMemo(() => {
        const result = new Map<string, Map<any, number>>();
        columns.forEach(column => {
            const counts = new Map<any, number>();
            reports.forEach(report => {
                const raw =
                    column.id === 'recommendation'
                        ? getRecommendationText(report)
                        : report[column.id];
                const key = isEmptyValue(raw) ? EMPTY_FILTER_VALUE : raw;
                counts.set(key, (counts.get(key) || 0) + 1);
            });
            result.set(column.id, counts);
        });
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, reports]);

    // Paginação
    const paginatedReports = filteredReports.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize
    );
    
    const totalPages = Math.max(1, Math.ceil(filteredReports.length / pageSize));

    const hasActiveFilters = Boolean(search) || Object.keys(filters).length > 0;

    const clearAllFilters = () => {
        setFilters({});
        setSearch('');
        setCurrentPage(0);
    };

    // Agregar todas as regras acionadas de todos os reports
    const aggregateRules = () => {
        const rulesMap = new Map<string, number>();
        
        reports.forEach(report => {
            if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                report.backend_reasons.forEach((rule: string) => {
                    rulesMap.set(rule, (rulesMap.get(rule) || 0) + 1);
                });
            }
        });
        
        return Array.from(rulesMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([rule, count]) => ({ rule, count }));
    };

    const handleBack = () => {
        // Voltar com cache - passar batchData de volta
        const batchData = location.state?.batchData;
        navigate(`/recommendation-engine/recommendations/${batchId}`, {
            state: { batchData } // Passar cache de volta
        });
    };

    return (
        <PageForm
            pageTitle={`Processos - Documento ${document}`}
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={loading}
            breadcrumb={[
                { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                { label: 'Detalhes do Lote', url: '#' },
                { label: 'Processos', url: '#' }
            ]}
            controlSlot={<></>}
        >
            {/* Botão Voltar */}
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                    <span>←</span>
                    <span>Voltar</span>
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                {/* Informações do Documento */}
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        📄 Informações do Documento
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Documento:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{document}</span>
                        </div>
                        <div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Total de Processos:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{filteredReports.length}</span>
                        </div>
                    </div>
                </div>

                {/* Dashboard: Resumo de Regras Acionadas */}
                {reports.length > 0 && aggregateRules().length > 0 && (
                    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-700 dark:to-slate-600 p-6 rounded-lg border border-blue-200 dark:border-slate-500">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                            <span className="text-2xl">📊</span>
                            <span>Dashboard - Regras Acionadas</span>
                            <span className="ml-auto text-sm font-normal text-gray-600 dark:text-gray-300">
                                {aggregateRules().length} regra(s) única(s)
                            </span>
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {aggregateRules().map(({ rule, count }, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-slate-600 hover:shadow-md transition-shadow">
                                    <div className="flex items-start gap-3 flex-1">
                                        <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">•</span>
                                        <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{rule}</span>
                                    </div>
                                    <span className="ml-3 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-semibold">
                                        {count}x
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grid com Filtros */}
                <TableToolbar
                    search={search}
                    onSearchChange={(term) => {
                        setSearch(term);
                        setCurrentPage(0);
                    }}
                    searchPlaceholder="Buscar em todas as colunas"
                    filteredCount={filteredReports.length}
                    totalCount={reports.length}
                    onClear={clearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                />

                <div 
                    className="overflow-auto" 
                    style={{ 
                        minHeight: '400px',
                        maxHeight: '600px'
                    }}
                >
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ width: 'max-content' }}>
                        <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                            <tr>
                                {columns.map(column => (
                                    <ColumnHeader
                                        key={column.id}
                                        column={column}
                                        reports={reports}
                                        activeFilters={filters[column.id] || []}
                                        valueCounts={valueCountsByColumn.get(column.id)}
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={toggleSort}
                                        onFilterChange={(value, checked) => handleFilterChange(column.id, value, checked)}
                                        onSetFilterValues={(values) => setFilterValues(column.id, values)}
                                        onClearFilter={() => clearFilter(column.id)}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedReports.map((report, idx) => (
                                <tr key={idx}>
                                    {columns.map(column => (
                                        <td key={column.id} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {column.id === 'recommendation' 
                                                ? getRecommendationBadge(report)
                                                : formatCellValue(report[column.id])
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            {paginatedReports.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={Math.max(1, columns.length)}
                                        className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        {hasActiveFilters
                                            ? 'Nenhum processo encontrado com os filtros aplicados'
                                            : 'Nenhum processo para exibir'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* AF-3418: paginação no mesmo padrão das outras telas (era Anterior/Próxima) */}
                <TablePaginationFooter
                    page={currentPage + 1}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page - 1)}
                    pageSize={pageSize}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(0);
                    }}
                    rangeStart={filteredReports.length === 0 ? 0 : currentPage * pageSize + 1}
                    rangeEnd={Math.min((currentPage + 1) * pageSize, filteredReports.length)}
                    totalRows={filteredReports.length}
                />
            </div>
        </PageForm>
    );
};

/**
 * Cabeçalho de coluna com ordenação e filtro.
 *
 * AF-3418: as colunas já eram declaradas com `sortable: true`, mas nada implementava a
 * ordenação — clicar no cabeçalho não fazia nada. Agora o rótulo é o botão de ordenar
 * (mesmo comportamento e iconografia da tela de monitoramento) e o filtro fica num
 * dropdown próprio ao lado.
 */
const ColumnHeader: React.FC<{
    column: any;
    reports: any[];
    activeFilters: any[];
    valueCounts?: Map<any, number>;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    onSort: (field: string) => void;
    onFilterChange: (value: any, checked: boolean) => void;
    onSetFilterValues: (values: any[]) => void;
    onClearFilter: () => void;
}> = ({
    column,
    reports,
    activeFilters,
    valueCounts,
    sortBy,
    sortOrder,
    onSort,
    onFilterChange,
    onSetFilterValues,
    onClearFilter,
}) => {
    const uniqueValues = React.useMemo(
        () => computeUniqueValues(column.id, reports),
        [reports, column.id]
    );

    const isSorted = sortBy === column.id;

    return (
        <th
            className="relative whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300"
            aria-sort={isSorted ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}
        >
            <div className="flex items-center justify-between gap-2">
                {column.sortable ? (
                    <button
                        type="button"
                        onClick={() => onSort(column.id)}
                        className="group inline-flex cursor-pointer select-none items-center gap-0.5 border-none bg-transparent p-0 text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-300 dark:hover:text-gray-100"
                        aria-label={`Ordenar por ${column.label}`}
                    >
                        {column.label}
                        {!isSorted && (
                            <span className="ml-1 text-gray-400 opacity-0 group-hover:opacity-100" aria-hidden="true">
                                &#x25B2;
                            </span>
                        )}
                        {isSorted && (
                            <span className="ml-1 text-blue-500" aria-hidden="true">
                                {sortOrder === 'ASC' ? '\u25B2' : '\u25BC'}
                            </span>
                        )}
                    </button>
                ) : (
                    <span>{column.label}</span>
                )}

                {column.filterable && (
                    <ColumnFilterDropdown
                        label={column.label}
                        values={uniqueValues}
                        activeValues={activeFilters}
                        counts={valueCounts}
                        onToggleValue={onFilterChange}
                        onSetValues={onSetFilterValues}
                        onClear={onClearFilter}
                        emptyValueToken={EMPTY_FILTER_VALUE}
                        emptyValueLabel={EMPTY_FILTER_LABEL}
                    />
                )}
            </div>
        </th>
    );
};

export default ProviderDocumentsGrid;

// Exported for testing
export { EMPTY_FILTER_VALUE, EMPTY_FILTER_LABEL, isEmptyValue };

/**
 * Pure function: compute unique filter values for a column.
 * Exported for unit/property-based testing.
 */
export function computeUniqueValues(columnId: string, reports: any[]): any[] {
    if (columnId === 'recommendation') {
        // AF-3460: inclui os estados em que nenhuma regra foi avaliada, para o analista
        // conseguir filtrar exatamente esses documentos
        return [
            'Aprovado',
            'Pendente',
            'Reprovado',
            'Sinalizado Crédito',
            'Sem dados',
            'Erro na análise',
        ];
    }

    const values = reports.map(r => r[columnId]).filter(v => !isEmptyValue(v));
    const sorted = Array.from(new Set(values)).sort();

    const hasEmpty = reports.some(r => isEmptyValue(r[columnId]));
    if (hasEmpty) {
        sorted.push(EMPTY_FILTER_VALUE);
    }

    return sorted;
}

/**
 * Helper: resolve recommendation text for a report.
 * Exported for reuse in applyFiltersPure and component.
 */
export function getRecommendationTextPure(report: any): string {
    // AF-3460/AF-3418: sem dados e erro são avaliados ANTES do credit_flag e não caem mais
    // no fallback 'Pendente'. Classificá-los como pendente fazia o filtro da coluna
    // esconder esses documentos dentro de um estado que não é o deles.
    if (report.backend_recommendation === 'SEM_DADOS') return 'Sem dados';
    if (report.backend_recommendation === 'ERRO') return 'Erro na análise';
    if (report.backend_credit_flag) return 'Sinalizado Crédito';
    if (report.backend_recommendation === 'SINALIZADO_CREDITO') return 'Sinalizado Crédito';
    if (report.backend_recommendation === 'APROVADO') return 'Aprovado';
    if (report.backend_recommendation === 'REPROVADO') return 'Reprovado';
    if (report.backend_recommendation === 'PENDENTE') return 'Pendente';
    return 'Pendente';
}

/**
 * Pure function: apply column filters to reports.
 * Exported for unit/property-based testing.
 */
export function applyFiltersPure(reports: any[], activeFilters: Record<string, any[]>): any[] {
    return reports.filter(report => {
        return Object.entries(activeFilters).every(([columnId, values]) => {
            if (values.length === 0) return true;

            if (columnId === 'recommendation') {
                const badge = getRecommendationTextPure(report);
                return values.includes(badge);
            }

            return values.includes(report[columnId]) ||
                (values.includes(EMPTY_FILTER_VALUE) && isEmptyValue(report[columnId]));
        });
    });
}
